/**
 * Хлебные крошки каркаса по макету-эталону (prototype.js:568-569): «Рабочее пространство ›
 * раздел», на странице объекта -- третьим уровнем его имя. Крошки строит каркас; страница
 * сообщает только хвост после раздела (useBreadcrumbs) и своих крошек не рисует.
 *
 * Раздел на странице объекта ведёт на последний список раздела вместе с его фильтрами, как
 * «Хранилища» макета (lastStorageRoute). Список -- адрес раздела, страница которого хвоста не
 * сообщала: с него пользователь и открыл объект. Уровней не больше трёх: у вложенной страницы
 * (кластер бакета, схема топика, образец) второй уровень -- ближайший родитель из её хвоста.
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { create } from 'zustand';
import { pageSection } from './navigation';

/** Крошка: подпись и адрес; у текущей страницы адреса нет */
export interface Crumb {
	label: string;
	to?: string;
}

/** Открытый адрес: раздел и то, сообщила ли его страница хвост (страница объекта, не список) */
interface Visit {
	href: string;
	section: string;
	detail: boolean;
}

interface BreadcrumbState {
	/** Хвост текущей страницы; null -- страница раздела, хвоста нет */
	tail: readonly Crumb[] | null;
	/** Чей хвост: уходящая страница снимает только свой */
	owner: symbol | null;
	current: Visit | null;
	previous: Visit | null;
	/** Последний список раздела: раздел -> адрес с параметрами фильтров */
	lists: Readonly<Record<string, string>>;
}

const INITIAL: BreadcrumbState = {
	tail: null,
	owner: null,
	current: null,
	previous: null,
	lists: {},
};

const useBreadcrumbStore = create<BreadcrumbState>(() => INITIAL);

/**
 * Только переход на другую страницу меняет previous. Канонизация query (например,
 * Provider добавляет training до загрузки lazy-карточки) сохраняет её классификацию
 * и настоящий предыдущий список; фильтры текущего списка при этом обновляются.
 */
function visited(
	state: BreadcrumbState,
	href: string,
	section: string,
): Pick<BreadcrumbState, 'current' | 'previous'> {
	if (state.current?.href === href) return { current: state.current, previous: state.previous };
	if (state.current?.href.split('?')[0] === href.split('?')[0]) {
		return { current: { ...state.current, href }, previous: state.previous };
	}
	return { previous: state.current, current: { href, section, detail: false } };
}

/**
 * Шапка отмечает каждый открытый адрес, страница с хвостом — тот же адрес через register.
 * visited не меняет запись для повторного href, поэтому результат не зависит от порядка
 * этих эффектов: признак detail, выставленный register, сохраняется.
 */
function visit(href: string, section: string): void {
	useBreadcrumbStore.setState((state) => visited(state, href, section));
}

function register(owner: symbol, tail: readonly Crumb[], href: string, section: string): void {
	useBreadcrumbStore.setState((state) => {
		const { current, previous } = visited(state, href, section);
		/* Объект открыт со списка своего раздела: этот список -- адрес возврата */
		const fromList = previous !== null && !previous.detail && previous.section === section;
		return {
			tail,
			owner,
			current: current && { ...current, detail: true },
			previous,
			lists: fromList ? { ...state.lists, [section]: previous.href } : state.lists,
		};
	});
}

function unregister(owner: symbol): void {
	useBreadcrumbStore.setState((state) =>
		state.owner === owner ? { tail: null, owner: null } : state,
	);
}

/** Выход или смена пользователя забывает адреса и фильтры прежней сессии */
export function resetBreadcrumbs(): void {
	useBreadcrumbStore.setState(INITIAL, true);
}

/**
 * Страница объекта сообщает каркасу хвост крошек после раздела: имя объекта, а у вложенной
 * страницы -- сначала родителя со ссылкой. Пустой хвост -- имя ещё загружается: страница
 * всё равно страница объекта, а не список раздела.
 */
export function useBreadcrumbs(items: readonly Crumb[]): void {
	const { pathname, search } = useLocation();
	const href = `${pathname}${search}`;
	/* Страница передаёт новый массив при каждой отрисовке: хвост сравнивается по содержанию */
	const key = JSON.stringify(items);

	useEffect(() => {
		const owner = Symbol('breadcrumbs');
		register(owner, JSON.parse(key) as Crumb[], href, pageSection(pathname).id);
		return () => unregister(owner);
	}, [key, href, pathname]);
}

/**
 * Последний список раздела текущей страницы вместе с его фильтрами: адрес «Все …» и возврата
 * после сохранения на странице объекта (спецификация С:159 — фильтры переживают навигацию)
 */
export function useSectionList(): string {
	const { pathname } = useLocation();
	const section = pageSection(pathname);
	const list = useBreadcrumbStore((state) => state.lists[section.id]);
	return list ?? section.to;
}

/**
 * Уровни крошек: корень, раздел или ближайший родитель, текущая страница. На странице раздела
 * раздел -- текущая страница; на странице объекта раздел ведёт на последний список раздела.
 */
export function shellBreadcrumbs(
	root: Crumb,
	section: Required<Crumb>,
	tail: readonly Crumb[] | null,
): Crumb[] {
	if (tail === null) return [root, { label: section.label }];
	const current = tail.at(-1);
	if (current === undefined) return [root, section];
	const nearest = tail.length > 1 ? tail.at(-2) : undefined;
	const parent = nearest?.to !== undefined ? nearest : section;
	return [root, { label: parent.label, to: parent.to }, { label: current.label }];
}

/**
 * Последний список раздела текущей страницы вместе с его фильтрами — тот же адрес, что у крошки
 * раздела (lastStorageRoute макета: «Все хранилища» карточки); список не открывался — адрес раздела
 */
export function useSectionListHref(): string {
	const { pathname } = useLocation();
	const section = pageSection(pathname);
	const list = useBreadcrumbStore((state) => state.lists[section.id]);
	return list ?? section.to;
}

/** Крошки шапки для текущего адреса */
export function useShellBreadcrumbs(): Crumb[] {
	const { t } = useTranslation('common');
	const { t: tNav } = useTranslation('nav');
	const { pathname, search } = useLocation();
	const href = `${pathname}${search}`;
	const section = pageSection(pathname);
	const tail = useBreadcrumbStore((state) => state.tail);
	const list = useBreadcrumbStore((state) => state.lists[section.id]);

	useEffect(() => visit(href, section.id), [href, section.id]);

	return shellBreadcrumbs(
		{ label: t('shell.workspaceRoot'), to: '/overview' },
		{ label: tNav(section.labelKey), to: list ?? section.to },
		tail,
	);
}
